import { createSlice, PayloadAction } from '@reduxjs/toolkit';

type UserId = string;
type LocationId = string;
type ProviderId = string;

interface CalendarPreferences {
  selectedUserId: string;
  selectedLocationId?: string;
}

interface LocationCalendarPreferences {
  selectedLocationId: string;
  providerSelectionsByLocation: Record<LocationId, ProviderId[]>; 
}

interface CalendarPreferencesState {
  preferences: Record<UserId, CalendarPreferences>; 
  locationPreferences: Record<UserId, LocationCalendarPreferences>;
}

const initialState: CalendarPreferencesState = {
  preferences: {},
  locationPreferences: {},
};

const calendarPreferencesSlice = createSlice({
  name: 'calendarPreferences',
  initialState,
  reducers: {
    setSelectedUser(state, action: PayloadAction<{ userId: UserId; selectedUserId: UserId }>) {
      const { userId, selectedUserId } = action.payload;
      if (userId) {
        if (!state.preferences[userId]) {
          state.preferences[userId] = { selectedUserId };
        } else {
          state.preferences[userId].selectedUserId = selectedUserId;
        }
      }
    },
    
    setSelectedLocation(state, action: PayloadAction<{ userId: UserId; selectedLocationId: LocationId }>) {
      const { userId, selectedLocationId } = action.payload;
      if (userId) {
        if (!state.preferences[userId]) {
          state.preferences[userId] = { selectedUserId: '', selectedLocationId };
        } else {
          state.preferences[userId].selectedLocationId = selectedLocationId;
        }
      }
    },
    
    setLocationPageLocation(state, action: PayloadAction<{ userId: UserId; selectedLocationId: LocationId }>) {
      const { userId, selectedLocationId } = action.payload;
      if (userId) {
        if (!state.locationPreferences[userId]) {
          state.locationPreferences[userId] = { 
            selectedLocationId,
            providerSelectionsByLocation: {}
          };
        } else {
          state.locationPreferences[userId].selectedLocationId = selectedLocationId;
        }
      }
    },
    
    setLocationPageProviders(state, action: PayloadAction<{ 
      userId: UserId; 
      locationId: LocationId; 
      selectedProviderIds: ProviderId[] 
    }>) {
      const { userId, locationId, selectedProviderIds } = action.payload;
      if (userId && locationId) {
        if (!state.locationPreferences[userId]) {
          state.locationPreferences[userId] = { 
            selectedLocationId: locationId,
            providerSelectionsByLocation: {
              [locationId]: selectedProviderIds
            }
          };
        } else {
          state.locationPreferences[userId].providerSelectionsByLocation[locationId] = selectedProviderIds;
        }
      }
    },
    
    clearAllPreferences(state) {
      state.preferences = {};
      state.locationPreferences = {};
    },
  },
});

export const { 
  setSelectedUser, 
  setSelectedLocation, 
  setLocationPageLocation, 
  setLocationPageProviders, 
  clearAllPreferences 
} = calendarPreferencesSlice.actions;

export default calendarPreferencesSlice.reducer;
